import React, { useEffect, useState, useCallback } from 'react';
import {
  BellIcon,
  PlusIcon,
  TrashIcon,
  PaperAirplaneIcon,
  PauseIcon,
  PlayIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';

// ── Types ────────────────────────────────────────────────────────────────────

interface NudgeSchedule {
  id: string;
  coach_client_id: number;
  schedule_type: 'one_off' | 'recurring';
  message: string;
  send_hour: number;
  send_minute: number;
  days_of_week: string | null;
  scheduled_dates: string | null;
  author_type: 'coach' | 'client' | 'ai';
  action_item_id: string | null;
  is_scheduled: boolean;
  is_active: boolean;
  expires_at: string;
  next_send_at: string | null;
  last_sent_at: string | null;
  created_at: string;
}

interface NudgeHistory {
  id: string;
  sent_at: string;
  status: string;
  channel: string;
  message_preview: string;
  client_name: string;
}

interface Props {
  coachClientId: number;
  coachToken: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const DAY_LABELS: Record<number, string> = {
  1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun',
};
const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 30];

function fmtTime(hour: number, minute: number): string {
  const ampm = hour < 12 ? 'AM' : 'PM';
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}:${minute === 0 ? '00' : '30'} ${ampm}`;
}

function fmtScheduleSummary(s: NudgeSchedule): string {
  if (s.schedule_type === 'recurring') {
    const days = (s.days_of_week || '').split(',').map(Number).filter(Boolean)
      .map(d => DAY_LABELS[d]).join(', ');
    return `Every ${days} · ${fmtTime(s.send_hour, s.send_minute)}`;
  }
  const dates: string[] = JSON.parse(s.scheduled_dates || '[]');
  const formatted = dates.map(d =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  ).join(', ');
  return `${formatted} · ${fmtTime(s.send_hour, s.send_minute)}`;
}

function fmtNextSend(iso: string | null): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' at ' + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function maxDate(): string {
  const d = new Date(); d.setDate(d.getDate() + 14);
  return d.toISOString().split('T')[0];
}
function minDate(): string { return new Date().toISOString().split('T')[0]; }

// ── Component ─────────────────────────────────────────────────────────────────

export default function FollowUpsTab({ coachClientId, coachToken }: Props) {
  const [scheduled, setScheduled] = useState<NudgeSchedule[]>([]);
  const [history, setHistory] = useState<NudgeHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingNow, setSendingNow] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formType, setFormType] = useState<'one_off' | 'recurring'>('recurring');
  const [formMessage, setFormMessage] = useState('');
  const [formHour, setFormHour] = useState(9);
  const [formMinute, setFormMinute] = useState(0);
  const [formDays, setFormDays] = useState<number[]>([]);
  const [formDates, setFormDates] = useState<string[]>([]);
  const [formDateInput, setFormDateInput] = useState('');

  // Reschedule state
  const [rescheduleId, setRescheduleId] = useState<string | null>(null);
  const [rescheduleType, setRescheduleType] = useState<'one_off' | 'recurring'>('one_off');
  const [rescheduleMessage, setRescheduleMessage] = useState('');
  const [rescheduleHour, setRescheduleHour] = useState(9);
  const [rescheduleMinute, setRescheduleMinute] = useState(0);
  const [rescheduleDays, setRescheduleDays] = useState<number[]>([]);
  const [rescheduleDates, setRescheduleDates] = useState<string[]>([]);
  const [rescheduleDateInput, setRescheduleDateInput] = useState('');
  const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);

  const base = config.apiBaseUrl;

  const fetchAll = useCallback(async () => {
    const hdrs = { Authorization: `Bearer ${coachToken}` };
    setLoading(true);
    try {
      const resp = await fetch(
        `${base}${endpoints.main.nudgeSchedules.list}?coach_client_id=${coachClientId}`,
        { headers: hdrs }
      );
      if (resp.ok) setScheduled(await resp.json());
    } finally {
      setLoading(false);
    }
  }, [coachClientId, base, coachToken]);

  const fetchHistory = useCallback(async () => {
    const hdrs = { Authorization: `Bearer ${coachToken}` };
    const resp = await fetch(
      `${base}${endpoints.main.nudgeSchedules.history}?coach_client_id=${coachClientId}`,
      { headers: hdrs }
    );
    if (resp.ok) setHistory(await resp.json());
  }, [coachClientId, base, coachToken]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (showHistory) fetchHistory(); }, [showHistory, fetchHistory]);

  function resetForm() {
    setFormType('recurring'); setFormMessage(''); setFormHour(9); setFormMinute(0);
    setFormDays([]); setFormDates([]); setFormDateInput(''); setError(null);
  }

  async function handleCreate(prefillMessage?: string) {
    setError(null);
    const msg = prefillMessage ?? formMessage;
    if (!msg.trim()) { setError('Message is required.'); return; }
    if (formType === 'recurring' && formDays.length === 0) { setError('Select at least one day.'); return; }
    if (formType === 'one_off' && formDates.length === 0) { setError('Add at least one date.'); return; }

    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        coach_client_id: coachClientId, schedule_type: formType,
        message: msg.trim(), send_hour: formHour, send_minute: formMinute,
      };
      if (formType === 'recurring') body.days_of_week = formDays;
      else body.scheduled_dates = formDates;

      const resp = await fetch(`${base}${endpoints.main.nudgeSchedules.create}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${coachToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json();
        setError(err.detail || 'Failed to create schedule.');
        return;
      }
      resetForm(); setShowForm(false); fetchAll();
    } finally { setSubmitting(false); }
  }

  async function toggleActive(s: NudgeSchedule) {
    await fetch(`${base}${endpoints.main.nudgeSchedules.update(s.id)}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${coachToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !s.is_active }),
    });
    fetchAll();
  }

  async function deleteSchedule(id: string) {
    await fetch(`${base}${endpoints.main.nudgeSchedules.delete(id)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${coachToken}` },
    });
    fetchAll();
  }

  async function sendNow(id: string) {
    setSendingNow(id);
    try {
      await fetch(`${base}${endpoints.main.nudgeSchedules.sendNow(id)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${coachToken}` },
      });
      fetchAll();
      if (showHistory) fetchHistory();
    } finally { setSendingNow(null); }
  }

  function addDate() {
    if (!formDateInput || formDates.includes(formDateInput)) return;
    setFormDates(prev => [...prev, formDateInput].sort());
    setFormDateInput('');
  }
  function toggleDay(d: number) {
    setFormDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
  }

  function openReschedule(s: NudgeSchedule) {
    setRescheduleId(s.id);
    setRescheduleType(s.schedule_type);
    setRescheduleMessage(s.message);
    setRescheduleHour(s.send_hour);
    setRescheduleMinute(s.send_minute);
    setRescheduleDays(
      s.schedule_type === 'recurring'
        ? (s.days_of_week || '').split(',').map(Number).filter(Boolean)
        : []
    );
    setRescheduleDates(
      s.schedule_type === 'one_off'
        ? JSON.parse(s.scheduled_dates || '[]')
        : []
    );
    setRescheduleDateInput('');
    setRescheduleError(null);
  }

  async function submitReschedule(id: string) {
    setRescheduleError(null);
    if (rescheduleType === 'recurring' && rescheduleDays.length === 0) {
      setRescheduleError('Select at least one day.');
      return;
    }
    if (rescheduleType === 'one_off' && rescheduleDates.length === 0) {
      setRescheduleError('Add at least one date.');
      return;
    }
    setRescheduleSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        message: rescheduleMessage.trim(),
        send_hour: rescheduleHour,
        send_minute: rescheduleMinute,
      };
      if (rescheduleType === 'recurring') body.days_of_week = rescheduleDays;
      else body.scheduled_dates = rescheduleDates;

      const resp = await fetch(`${base}${endpoints.main.nudgeSchedules.update(id)}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${coachToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json();
        setRescheduleError(err.detail || 'Failed to reschedule.');
        return;
      }
      setRescheduleId(null);
      fetchAll();
    } finally {
      setRescheduleSubmitting(false);
    }
  }

  if (loading) return <div className="p-5 text-gray-400 text-sm">Loading follow-ups…</div>;

  return (
    <div className="p-5 space-y-5">

      {/* ── Scheduled Nudges header ─────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
          <BellIcon className="w-4 h-4" />
          Scheduled Nudges
        </h3>
        <button
          onClick={() => { resetForm(); setShowForm(v => !v); }}
          className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          New nudge
        </button>
      </div>

      {/* ── Create / Schedule form ──────────────────────────────────────── */}
      {showForm && (
        <div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50 space-y-3 text-sm">
          {error && <p className="text-red-600 text-xs">{error}</p>}

          <div className="flex gap-3">
            {(['recurring', 'one_off'] as const).map(t => (
              <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" checked={formType === t} onChange={() => setFormType(t)} />
                <span className="text-gray-700">{t === 'recurring' ? 'Recurring' : 'One-off'}</span>
              </label>
            ))}
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Message</label>
            <textarea
              value={formMessage}
              onChange={e => setFormMessage(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="Write a nudge for this client…"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <p className="text-right text-xs text-gray-400">{formMessage.length}/1000</p>
          </div>

          <div className="flex gap-2 items-center">
            <label className="text-xs text-gray-500 w-10">Time</label>
            <select value={formHour} onChange={e => setFormHour(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm">
              {HOURS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
            <select value={formMinute} onChange={e => setFormMinute(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm">
              {MINUTES.map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
            </select>
          </div>

          {formType === 'recurring' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Days</label>
              <div className="flex gap-1.5 flex-wrap">
                {ALL_DAYS.map(d => (
                  <button key={d} type="button" onClick={() => toggleDay(d)}
                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                      formDays.includes(d)
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                    }`}>
                    {DAY_LABELS[d]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {formType === 'one_off' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Dates (max 14 days out)</label>
              <div className="flex gap-2">
                <input type="date" value={formDateInput} min={minDate()} max={maxDate()}
                  onChange={e => setFormDateInput(e.target.value)}
                  className="bg-dark-canvas border border-dark-border rounded px-2 py-1 text-sm text-gray-100 [color-scheme:dark]" />
                <button type="button" onClick={addDate}
                  className="px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700">
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {formDates.map(d => (
                  <span key={d} className="flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">
                    {d}
                    <button onClick={() => setFormDates(prev => prev.filter(x => x !== d))}>
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button onClick={() => handleCreate()} disabled={submitting}
              className="px-4 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 disabled:opacity-50">
              {submitting ? 'Scheduling…' : 'Schedule'}
            </button>
            <button onClick={() => { setShowForm(false); resetForm(); }}
              className="px-4 py-1.5 border border-gray-300 text-gray-600 text-xs rounded hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Scheduled list ──────────────────────────────────────────────── */}
      {scheduled.length === 0 && !showForm && (
        <p className="text-gray-400 text-sm">No nudges scheduled yet.</p>
      )}

      <div className="space-y-2">
        {scheduled.map(s => (
          <div key={s.id}
            className={`border rounded-lg p-3.5 ${s.is_active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-gray-800 flex-1 leading-snug">{s.message}</p>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  s.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s.is_active ? 'Active' : 'Paused'}
                </span>
              </div>
            </div>
            <p className="text-xs text-indigo-600 mt-1">{fmtScheduleSummary(s)}</p>
            {s.next_send_at && (
              <p className="text-xs text-gray-400 mt-0.5">Next: {fmtNextSend(s.next_send_at)}</p>
            )}
            {!s.next_send_at && !s.is_active && (
              <p className="text-xs text-gray-400 mt-0.5">Exhausted</p>
            )}
            <div className="flex gap-2 mt-2.5">
              <button onClick={() => sendNow(s.id)} disabled={sendingNow === s.id}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 px-2 py-1 border border-gray-200 rounded hover:border-indigo-300 transition-colors">
                <PaperAirplaneIcon className="w-3.5 h-3.5" />
                {sendingNow === s.id ? 'Sending…' : 'Send now'}
              </button>
              <button onClick={() => toggleActive(s)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-amber-600 px-2 py-1 border border-gray-200 rounded hover:border-amber-300 transition-colors">
                {s.is_active
                  ? <><PauseIcon className="w-3.5 h-3.5" /> Pause</>
                  : <><PlayIcon className="w-3.5 h-3.5" /> Resume</>
                }
              </button>
              <button onClick={() => openReschedule(s)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 px-2 py-1 border border-gray-200 rounded hover:border-indigo-300 transition-colors">
                <PencilIcon className="w-3.5 h-3.5" />
                Reschedule
              </button>
              <button onClick={() => deleteSchedule(s.id)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 px-2 py-1 border border-gray-200 rounded hover:border-red-300 transition-colors ml-auto">
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* ── Inline reschedule form ── */}
            {rescheduleId === s.id && (
              <div className="mt-3 border border-indigo-200 rounded-lg p-3 bg-indigo-50 space-y-2 text-sm">
                {rescheduleError && <p className="text-red-600 text-xs">{rescheduleError}</p>}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Message</label>
                  <textarea
                    value={rescheduleMessage}
                    onChange={e => setRescheduleMessage(e.target.value)}
                    rows={2}
                    maxLength={1000}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  />
                </div>
                <div className="flex gap-3">
                  {(['one_off', 'recurring'] as const).map(t => (
                    <label key={t} className="flex items-center gap-1.5 cursor-pointer text-xs">
                      <input type="radio" checked={rescheduleType === t} onChange={() => setRescheduleType(t)} />
                      {t === 'one_off' ? 'One-off' : 'Recurring'}
                    </label>
                  ))}
                </div>
                {rescheduleType === 'recurring' ? (
                  <div className="flex gap-1 flex-wrap">
                    {ALL_DAYS.map(d => (
                      <button key={d} type="button"
                        onClick={() => setRescheduleDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort())}
                        className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                          rescheduleDays.includes(d)
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                        }`}>
                        {DAY_LABELS[d]}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div>
                    <div className="flex gap-2">
                      <input type="date" value={rescheduleDateInput} min={minDate()} max={maxDate()}
                        onChange={e => setRescheduleDateInput(e.target.value)}
                        className="bg-dark-canvas border border-dark-border rounded px-2 py-1 text-sm text-gray-100 [color-scheme:dark]" />
                      <button type="button"
                        onClick={() => {
                          if (rescheduleDateInput && !rescheduleDates.includes(rescheduleDateInput)) {
                            setRescheduleDates(prev => [...prev, rescheduleDateInput].sort());
                            setRescheduleDateInput('');
                          }
                        }}
                        className="px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700">
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {rescheduleDates.map(d => (
                        <span key={d} className="flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">
                          {d}
                          <button onClick={() => setRescheduleDates(prev => prev.filter(x => x !== d))}>
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 items-center">
                  <label className="text-xs text-gray-500 w-10">Time</label>
                  <select value={rescheduleHour} onChange={e => setRescheduleHour(Number(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1 text-sm">
                    {HOURS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                  </select>
                  <select value={rescheduleMinute} onChange={e => setRescheduleMinute(Number(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1 text-sm">
                    {MINUTES.map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
                  </select>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => submitReschedule(s.id)} disabled={rescheduleSubmitting}
                    className="px-4 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 disabled:opacity-50">
                    {rescheduleSubmitting ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => setRescheduleId(null)}
                    className="px-4 py-1.5 border border-gray-300 text-gray-600 text-xs rounded hover:bg-gray-50">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── History ─────────────────────────────────────────────────────── */}
      <button onClick={() => setShowHistory(v => !v)}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
        {showHistory ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />}
        {showHistory ? 'Hide' : 'Show'} history
      </button>

      {showHistory && (
        <div className="border-t pt-3 space-y-1.5">
          {history.length === 0 && <p className="text-xs text-gray-400">No nudges sent yet.</p>}
          {history.map(h => (
            <div key={h.id} className="flex items-start justify-between text-xs text-gray-600 gap-2">
              <span className="flex-1 truncate">{h.message_preview}</span>
              <span className="text-gray-400 shrink-0">
                {new Date(h.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <span className={`shrink-0 ${h.status === 'sent' ? 'text-green-600' : 'text-red-500'}`}>
                {h.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
